import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Observable, filter, map, shareReplay, tap } from 'rxjs';

/**
 * Catalogs that every screen asks for again on each visit and that change rarely: task types, statuses, tags, points
 * of sale, products, colors, identification types, users, providers. The calendar alone asked for 9 of them (Tags and
 * users twice each, in parallel), every time it was opened.
 *
 * - Identical concurrent GETs share one request.
 * - A 200 answer is reused for TTL_MS within the browser tab (memory only; a reload or logout starts empty).
 * - Any POST/PUT/PATCH/DELETE to the same API resource drops its cached answers, so this tab sees its own edits at once.
 * - Each subscriber gets its own copy of the body: a screen that sorts or edits the list cannot change another's.
 */
const TTL_MS = 5 * 60 * 1000;
const REFERENCE_GET =
  /\/api\/(tasks\/status|Tags|TaskGroups|PointOfSales|Clients\/IdentificationTypes|Products|products\/colors|users|providers)(\?|$)/i;

interface Entry {
  expires: number;
  response$: Observable<HttpResponse<unknown>>;
}

@Injectable()
export class ReferenceDataCacheInterceptor implements HttpInterceptor {
  private readonly cache = new Map<string, Entry>();

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (req.method !== 'GET') {
      this.invalidate(req.url);
      return next.handle(req);
    }
    if (!REFERENCE_GET.test(req.urlWithParams)) {
      return next.handle(req);
    }

    const key = req.urlWithParams;
    let entry = this.cache.get(key);
    if (!entry || entry.expires < Date.now()) {
      const response$ = next.handle(req).pipe(
        filter((event): event is HttpResponse<unknown> => event instanceof HttpResponse),
        tap({
          next: (response) => {
            if (response.status !== 200) this.cache.delete(key);
          },
          error: () => this.cache.delete(key),
        }),
        shareReplay(1)
      );
      entry = { expires: Date.now() + TTL_MS, response$ };
      this.cache.set(key, entry);
    }
    return entry.response$.pipe(map((response) => response.clone({ body: structuredClone(response.body) })));
  }

  /** .../api/TaskGroups/123/visible drops every cached .../api/TaskGroups... answer (case-insensitive). */
  private invalidate(url: string) {
    const match = /^(.*\/api\/[^/?]+)/i.exec(url);
    if (!match) return;
    const resource = match[1].toLowerCase();
    for (const key of [...this.cache.keys()]) {
      if (key.toLowerCase().startsWith(resource)) this.cache.delete(key);
    }
  }
}
